import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import portalApi from '@/lib/portalApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Car, Wrench, FileText, DollarSign, ChevronRight, Calendar, AlertCircle, CheckCircle2, Clock, MapPin, Phone, Mail, KeyRound, MessageCircle, History, Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import HistoricoVeiculoModal from '@/components/HistoricoVeiculoModal';

const fmtBRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtDate = (d) => d ? new Date(d.includes('T') ? d : d + 'T00:00:00').toLocaleDateString('pt-BR') : '-';

const STATUS_ORDEM = {
  em_andamento: { label: 'Em andamento', cor: 'bg-amber-500/20 text-amber-300 border-amber-500/30', icon: Clock },
  concluido: { label: 'Concluído', cor: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30', icon: CheckCircle2 },
  finalizada: { label: 'Concluído', cor: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30', icon: CheckCircle2 },
  aguardando: { label: 'Aguardando', cor: 'bg-blue-500/20 text-blue-300 border-blue-500/30', icon: Clock },
  cancelado: { label: 'Cancelada', cor: 'bg-red-500/20 text-red-300 border-red-500/30', icon: AlertCircle },
  cancelada: { label: 'Cancelada', cor: 'bg-red-500/20 text-red-300 border-red-500/30', icon: AlertCircle },
};

const STATUS_ORC = {
  pendente: { label: 'Aguardando aprovação', cor: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
  aprovado: { label: 'Aprovado', cor: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' },
  recusado: { label: 'Recusado', cor: 'bg-red-500/20 text-red-300 border-red-500/30' },
  convertido: { label: 'Convertido em OS', cor: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
};

function PageHeader({ icon: Icon, titulo, subtitulo }) {
  return (
    <div className="mb-5">
      <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
        {Icon && <Icon className="w-6 h-6 md:w-7 md:h-7 text-blue-400" />}
        {titulo}
      </h1>
      {subtitulo && <p className="text-slate-400 text-xs md:text-sm mt-1">{subtitulo}</p>}
    </div>
  );
}

// ====== HOME (Dashboard) ======
export function ClienteHome() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    portalApi.get('/cliente/me').then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return <div className="p-4 text-slate-400">Carregando...</div>;

  return (
    <div className="p-4 md:p-8 max-w-[1200px] mx-auto" data-testid="portal-home">
      <div className="mb-6">
        <p className="text-blue-300/70 text-sm">Olá,</p>
        <h1 className="text-2xl md:text-3xl font-bold text-white">{data.cliente.nome} 👋</h1>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="bg-gradient-to-br from-blue-500/20 to-blue-700/10 border border-blue-500/30 rounded-xl p-4">
          <p className="text-[10px] md:text-xs text-slate-400 uppercase tracking-wider">Veículos</p>
          <p className="text-2xl font-bold text-white mt-1">{data.veiculos.length}</p>
        </div>
        <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-700/10 border border-emerald-500/30 rounded-xl p-4">
          <p className="text-[10px] md:text-xs text-slate-400 uppercase tracking-wider">Total pago</p>
          <p className="text-lg md:text-2xl font-bold text-emerald-400 mt-1 truncate">{fmtBRL(data.financeiro.total_pago)}</p>
        </div>
      </div>

      {data.financeiro.total_pendente > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-5 flex items-center justify-between gap-3">
          <div>
            <p className="text-amber-300 text-xs font-semibold uppercase tracking-wider">Pendente</p>
            <p className="text-xl font-bold text-amber-400">{fmtBRL(data.financeiro.total_pendente)}</p>
          </div>
          <Button size="sm" variant="ghost" onClick={() => navigate('/oficina/cliente/financeiro')} className="text-amber-300 border border-amber-500/30">Ver detalhes</Button>
        </div>
      )}

      {/* Veículos */}
      {data.veiculos.length > 0 && (
        <div className="mb-6">
          <h2 className="text-white font-semibold mb-3 flex items-center gap-2"><Car className="w-4 h-4 text-blue-400" /> Meus Veículos</h2>
          <div className="space-y-2">
            {data.veiculos.slice(0, 3).map(v => (
              <button key={v.id} onClick={() => navigate('/oficina/cliente/veiculos')} className="w-full text-left bg-slate-900 border border-white/10 rounded-xl p-3 flex items-center justify-between hover:border-blue-500/30">
                <div>
                  <p className="text-white text-sm font-medium">{v.marca} {v.modelo}</p>
                  <p className="text-xs text-slate-400">{v.placa}{v.ano ? ` · ${v.ano}` : ''}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-500" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Últimas OS */}
      {data.ultimas_ordens.length > 0 && (
        <div className="mb-6">
          <h2 className="text-white font-semibold mb-3 flex items-center gap-2"><Wrench className="w-4 h-4 text-blue-400" /> Últimos Serviços</h2>
          <div className="space-y-2">
            {data.ultimas_ordens.slice(0, 3).map(o => {
              const st = STATUS_ORDEM[o.status] || STATUS_ORDEM.em_andamento;
              return (
                <div key={o.id} className="bg-slate-900 border border-white/10 rounded-xl p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-white text-sm font-medium truncate">{o.descricao || 'Ordem de Serviço'}</p>
                      <p className="text-xs text-slate-500 mt-0.5"><Calendar className="w-3 h-3 inline mr-1" />{fmtDate(o.data_inicio)}</p>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${st.cor} shrink-0`}>{st.label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Orçamentos pendentes */}
      {data.orcamentos_pendentes.length > 0 && (
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
          <p className="text-blue-300 font-semibold flex items-center gap-2 mb-2"><FileText className="w-4 h-4" /> {data.orcamentos_pendentes.length} orçamento(s) aguardando</p>
          <Button size="sm" onClick={() => navigate('/oficina/cliente/orcamentos')} className="bg-blue-600 hover:bg-blue-500 text-white">Ver orçamentos</Button>
        </div>
      )}
    </div>
  );
}

// ====== VEÍCULOS ======
export function ClienteVeiculos() {
  const [items, setItems] = useState([]);
  const [historicoVeiculo, setHistoricoVeiculo] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ placa: '', marca: '', modelo: '', ano: '', cor: '' });
  const [saving, setSaving] = useState(false);

  const carregar = () => portalApi.get('/cliente/veiculos').then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { carregar(); }, []);

  const openNovo = () => {
    setEditing(null);
    setForm({ placa: '', marca: '', modelo: '', ano: '', cor: '' });
    setFormOpen(true);
  };

  const openEdit = (v) => {
    setEditing(v);
    setForm({ placa: v.placa || '', marca: v.marca || '', modelo: v.modelo || '', ano: v.ano || '', cor: v.cor || '' });
    setFormOpen(true);
  };

  const salvar = async () => {
    if (!form.placa || !form.marca || !form.modelo) {
      toast.error('Placa, Marca e Modelo são obrigatórios');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        placa: form.placa.toUpperCase(),
        marca: form.marca,
        modelo: form.modelo,
        ano: form.ano ? parseInt(form.ano, 10) : null,
        cor: form.cor || null,
      };
      if (editing) {
        await portalApi.put(`/cliente/veiculos/${editing.id}`, payload);
        toast.success('Veículo atualizado');
      } else {
        await portalApi.post('/cliente/veiculos', payload);
        toast.success('Veículo adicionado');
      }
      setFormOpen(false);
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const excluir = async (v) => {
    if (!window.confirm(`Excluir o veículo ${v.marca} ${v.modelo} (${v.placa})?`)) return;
    try {
      await portalApi.delete(`/cliente/veiculos/${v.id}`);
      toast.success('Veículo removido');
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao excluir');
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-[1200px] mx-auto" data-testid="portal-veiculos">
      <div className="flex items-start justify-between mb-5">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
            <Car className="w-6 h-6 md:w-7 md:h-7 text-blue-400" /> Meus Veículos
          </h1>
          <p className="text-slate-400 text-xs md:text-sm mt-1">{items.length} veículo(s) cadastrado(s)</p>
        </div>
        <Button onClick={openNovo} className="bg-blue-500 hover:bg-blue-600 text-white" data-testid="add-veiculo-cliente">
          <Plus className="w-4 h-4 mr-1" /> Adicionar
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="bg-slate-900 border border-white/10 rounded-xl py-12 text-center text-slate-500">
          <Car className="w-10 h-10 mx-auto mb-2 text-slate-600" />
          <p>Nenhum veículo cadastrado</p>
          <p className="text-xs mt-1 text-slate-600">Clique em "Adicionar" para cadastrar seu primeiro</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map(v => (
            <div key={v.id} className="bg-slate-900 border border-white/10 rounded-xl p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-white text-base font-semibold">{v.marca} {v.modelo}</p>
                  <p className="text-sm text-slate-400">Placa: <span className="text-white font-mono">{v.placa}</span></p>
                  {v.ano && <p className="text-xs text-slate-500">Ano: {v.ano}{v.cor ? ` · Cor: ${v.cor}` : ''}</p>}
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(v)} className="h-8 w-8 p-0 text-slate-400 hover:text-blue-400" data-testid={`edit-veiculo-${v.id}`}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  {v.created_by_cliente && (
                    <Button size="sm" variant="ghost" onClick={() => excluir(v)} className="h-8 w-8 p-0 text-slate-400 hover:text-red-400" data-testid={`del-veiculo-${v.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
              {v.ultima_manutencao && (
                <div className="mt-3 pt-3 border-t border-white/5">
                  <p className="text-xs text-slate-500">Última manutenção:</p>
                  <p className="text-sm text-white">{fmtDate(v.ultima_manutencao)}</p>
                  {v.ultima_descricao && <p className="text-xs text-slate-400 truncate">{v.ultima_descricao}</p>}
                </div>
              )}
              <div className="mt-3 pt-3 border-t border-white/5">
                <Button
                  onClick={() => setHistoricoVeiculo(v)}
                  className="w-full bg-blue-500/10 hover:bg-blue-500/20 text-blue-300 border border-blue-500/30"
                  data-testid={`historico-btn-${v.id}`}
                >
                  <History className="w-4 h-4 mr-2" /> Ver Histórico do Veículo
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal adicionar/editar veiculo */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-md" data-testid="veiculo-form-modal">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Veículo' : 'Adicionar Veículo'}</DialogTitle>
            <DialogDescription className="text-slate-400">
              {editing ? 'Atualize os dados do seu veículo' : 'Cadastre um novo veículo no seu perfil'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-slate-400 text-xs uppercase">Placa *</Label>
              <Input value={form.placa} onChange={(e) => setForm({ ...form, placa: e.target.value.toUpperCase() })}
                className="bg-slate-950 border-white/10 text-white font-mono" placeholder="ABC1234" maxLength={8} data-testid="veiculo-placa" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-slate-400 text-xs uppercase">Marca *</Label>
                <Input value={form.marca} onChange={(e) => setForm({ ...form, marca: e.target.value })}
                  className="bg-slate-950 border-white/10 text-white" placeholder="Fiat" data-testid="veiculo-marca" />
              </div>
              <div>
                <Label className="text-slate-400 text-xs uppercase">Modelo *</Label>
                <Input value={form.modelo} onChange={(e) => setForm({ ...form, modelo: e.target.value })}
                  className="bg-slate-950 border-white/10 text-white" placeholder="Uno" data-testid="veiculo-modelo" />
              </div>
              <div>
                <Label className="text-slate-400 text-xs uppercase">Ano</Label>
                <Input type="number" value={form.ano} onChange={(e) => setForm({ ...form, ano: e.target.value })}
                  className="bg-slate-950 border-white/10 text-white" placeholder="2020" />
              </div>
              <div>
                <Label className="text-slate-400 text-xs uppercase">Cor</Label>
                <Input value={form.cor} onChange={(e) => setForm({ ...form, cor: e.target.value })}
                  className="bg-slate-950 border-white/10 text-white" placeholder="Prata" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setFormOpen(false)} className="text-slate-400">Cancelar</Button>
              <Button onClick={salvar} disabled={saving} className="bg-blue-500 hover:bg-blue-600 text-white" data-testid="veiculo-salvar">
                {saving ? 'Salvando...' : (editing ? 'Atualizar' : 'Adicionar')}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <HistoricoVeiculoModal
        open={!!historicoVeiculo}
        onOpenChange={(o) => !o && setHistoricoVeiculo(null)}
        veiculo={historicoVeiculo}
        apiClient={portalApi}
        mode="cliente"
        basePathListar="/cliente/veiculos/{id}/historico"
        basePathCrud="/cliente/historico"
      />
    </div>
  );
}

// ====== SERVIÇOS / OS ======
export function ClienteServicos() {
  const [items, setItems] = useState([]);
  useEffect(() => { portalApi.get('/cliente/ordens').then(({ data }) => setItems(data)).catch(() => {}); }, []);

  return (
    <div className="p-4 md:p-8 max-w-[1200px] mx-auto" data-testid="portal-servicos">
      <PageHeader icon={Wrench} titulo="Histórico de Serviços" subtitulo={`${items.length} ordem(ns) de serviço`} />
      {items.length === 0 ? (
        <div className="bg-slate-900 border border-white/10 rounded-xl py-12 text-center text-slate-500">Nenhum serviço registrado</div>
      ) : (
        <div className="space-y-3">
          {items.map(o => {
            const st = STATUS_ORDEM[o.status] || STATUS_ORDEM.em_andamento;
            const StIcon = st.icon;
            return (
              <div key={o.id} className="bg-slate-900 border border-white/10 rounded-xl p-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-white font-medium">{o.descricao || 'Ordem de Serviço'}</p>
                    <p className="text-xs text-slate-500 mt-1">{o.veiculo_info}</p>
                    <p className="text-xs text-slate-500 flex items-center gap-1 mt-1"><Calendar className="w-3 h-3" /> {fmtDate(o.data_inicio)}{o.data_fim ? ` → ${fmtDate(o.data_fim)}` : ''}</p>
                  </div>
                  <span className={`text-[11px] px-2 py-1 rounded-full border flex items-center gap-1 ${st.cor} shrink-0`}>
                    <StIcon className="w-3 h-3" /> {st.label}
                  </span>
                </div>
                {o.items && o.items.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-white/5 space-y-1">
                    {o.items.map((it, i) => (
                      <div key={`${o.id}-it-${i}`} className="flex justify-between text-xs">
                        <span className="text-slate-400">{it.descricao || it.nome} {it.quantidade ? `(${it.quantidade}x)` : ''}</span>
                        <span className="text-white">{fmtBRL(it.valor_total || (it.valor_unitario * (it.quantidade || 1)))}</span>
                      </div>
                    ))}
                  </div>
                )}
                {o.valor_total && (
                  <div className="mt-3 pt-3 border-t border-white/5 flex justify-between items-center">
                    <span className="text-slate-400 text-sm">Total</span>
                    <span className="text-emerald-400 font-bold">{fmtBRL(o.valor_total)}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ====== ORÇAMENTOS ======
export function ClienteOrcamentos() {
  const [items, setItems] = useState([]);
  useEffect(() => { portalApi.get('/cliente/orcamentos').then(({ data }) => setItems(data)).catch(() => {}); }, []);

  return (
    <div className="p-4 md:p-8 max-w-[1200px] mx-auto" data-testid="portal-orcamentos">
      <PageHeader icon={FileText} titulo="Orçamentos" subtitulo={`${items.length} orçamento(s)`} />
      {items.length === 0 ? (
        <div className="bg-slate-900 border border-white/10 rounded-xl py-12 text-center text-slate-500">Nenhum orçamento</div>
      ) : (
        <div className="space-y-3">
          {items.map(o => {
            const st = STATUS_ORC[o.status] || STATUS_ORC.pendente;
            return (
              <div key={o.id} className="bg-slate-900 border border-white/10 rounded-xl p-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-white font-medium">Orçamento #{String(o.id).slice(-6)}</p>
                    <p className="text-xs text-slate-500 mt-1"><Calendar className="w-3 h-3 inline mr-1" /> {fmtDate(o.created_at || o.data)}</p>
                    {o.observacoes && <p className="text-xs text-slate-400 mt-1 truncate">{o.observacoes}</p>}
                  </div>
                  <span className={`text-[11px] px-2 py-1 rounded-full border ${st.cor} shrink-0`}>{st.label}</span>
                </div>
                {o.items && (
                  <div className="mt-3 pt-3 border-t border-white/5 space-y-1">
                    {o.items.map((it, i) => (
                      <div key={`${o.id}-item-${i}`} className="flex justify-between text-xs">
                        <span className="text-slate-400">{it.descricao} ({it.quantidade}x)</span>
                        <span className="text-white">{fmtBRL(it.valor_total)}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-3 pt-3 border-t border-white/5 flex justify-between items-center">
                  <span className="text-slate-400 text-sm">Valor total</span>
                  <span className="text-emerald-400 font-bold">{fmtBRL(o.valor_total)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ====== FINANCEIRO ======
export function ClienteFinanceiro() {
  const [data, setData] = useState({ transacoes: [], total_pago: 0, total_pendente: 0, parcelas_pendentes: [] });
  const [solicitando, setSolicitando] = useState(null); // transação selecionada
  const [forma, setForma] = useState('pix');
  const [observacao, setObservacao] = useState('');
  const [enviando, setEnviando] = useState(false);

  const carregar = () => portalApi.get('/cliente/financeiro').then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { carregar(); }, []);

  const FORMAS_LABEL = { dinheiro: 'Dinheiro', pix: 'PIX', cartao_debito: 'Cartão Débito', cartao_credito: 'Cartão Crédito', transferencia: 'Transferência', boleto: 'Boleto', crypto: 'Criptomoeda' };

  const solicitarPagamento = async () => {
    if (!solicitando) return;
    setEnviando(true);
    try {
      await portalApi.post(`/cliente/transacoes/${solicitando.id}/solicitar-pagamento`, { forma, observacao });
      toast.success('Pagamento solicitado! A oficina vai confirmar em breve.');
      setSolicitando(null); setForma('pix'); setObservacao('');
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao solicitar');
    } finally {
      setEnviando(false);
    }
  };

  const parcelas = data.parcelas_pendentes || data.transacoes.filter(t => t.status === 'pendente' || t.status === 'atrasado');

  return (
    <div className="p-4 md:p-8 max-w-[1200px] mx-auto" data-testid="portal-financeiro">
      <PageHeader icon={DollarSign} titulo="Meu Financeiro" subtitulo="Pagamentos, parcelas e saldo devedor" />

      {/* Saldo devedor destacado */}
      {(data.saldo_devedor || data.total_pendente) > 0 && (
        <div className="bg-gradient-to-r from-amber-600/20 to-red-600/10 border border-amber-500/40 rounded-xl p-5 mb-5">
          <p className="text-[11px] uppercase tracking-wider text-amber-300/80">Saldo devedor</p>
          <p className="text-3xl md:text-4xl font-bold text-amber-300 mt-1">{fmtBRL(data.saldo_devedor || data.total_pendente)}</p>
          <p className="text-xs text-slate-300 mt-2">{parcelas.length} parcela(s)/cobrança(s) pendente(s)</p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
          <p className="text-[10px] md:text-xs text-slate-400 uppercase tracking-wider">Total Pago</p>
          <p className="text-lg md:text-2xl font-bold text-emerald-400 mt-1 truncate">{fmtBRL(data.total_pago)}</p>
        </div>
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
          <p className="text-[10px] md:text-xs text-slate-400 uppercase tracking-wider">Pendente</p>
          <p className="text-lg md:text-2xl font-bold text-amber-400 mt-1 truncate">{fmtBRL(data.total_pendente)}</p>
        </div>
      </div>

      {/* Parcelas a pagar — destacadas */}
      {parcelas.length > 0 && (
        <div className="mb-5">
          <h2 className="text-white text-sm font-semibold mb-2 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-amber-400" /> Parcelas a pagar
          </h2>
          <div className="space-y-2">
            {parcelas.map((p) => (
              <div key={p.id} className="bg-slate-900 border border-amber-500/20 rounded-xl p-3" data-testid={`parcela-${p.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-white text-sm font-medium truncate">{p.descricao || 'Parcela'}</p>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-slate-400 mt-0.5">
                      <span>Venc: {fmtDate(p.data_vencimento || p.data)}</span>
                      {p.parcela_total > 1 && <span>Parcela {p.parcela_numero}/{p.parcela_total}</span>}
                      {p.status_solicitacao === 'aguardando_confirmacao' && (
                        <span className="text-blue-300">Aguardando confirmação</span>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-bold text-amber-400 text-lg">{fmtBRL(p.valor)}</p>
                  </div>
                </div>
                {p.status_solicitacao !== 'aguardando_confirmacao' && (
                  <Button onClick={() => setSolicitando(p)} className="w-full mt-2 bg-blue-500 hover:bg-blue-600 text-white text-sm h-8" data-testid={`pagar-btn-${p.id}`}>
                    <DollarSign className="w-3.5 h-3.5 mr-1" /> Pagar agora
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Histórico completo */}
      <h2 className="text-white text-sm font-semibold mb-2">Histórico</h2>
      {data.transacoes.length === 0 ? (
        <div className="bg-slate-900 border border-white/10 rounded-xl py-12 text-center text-slate-500">Nenhuma transação</div>
      ) : (
        <div className="space-y-2">
          {data.transacoes.map(t => (
            <div key={t.id} className="bg-slate-900 border border-white/10 rounded-xl p-3 flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-white text-sm font-medium truncate">{t.descricao || 'Pagamento'}</p>
                <p className="text-xs text-slate-500 mt-0.5">{fmtDate(t.data)} · {FORMAS_LABEL[t.forma_pagamento] || t.forma_pagamento || '—'}</p>
              </div>
              <div className="text-right shrink-0">
                <p className={`font-bold ${t.status === 'pago' ? 'text-emerald-400' : 'text-amber-400'}`}>{fmtBRL(t.valor)}</p>
                <p className="text-[10px] uppercase text-slate-500">{t.status}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal solicitar pagamento */}
      <Dialog open={!!solicitando} onOpenChange={(o) => { if (!o) setSolicitando(null); }}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">Pagar parcela</DialogTitle>
            <DialogDescription className="text-slate-400">
              {solicitando?.descricao} — <span className="text-white font-semibold">{fmtBRL(solicitando?.valor)}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-400 uppercase">Forma de pagamento</Label>
              <select value={forma} onChange={(e) => setForma(e.target.value)}
                className="w-full bg-slate-800 border border-white/10 text-white text-sm rounded p-2 mt-1" data-testid="solicitar-pagto-forma">
                <option value="pix">PIX</option>
                <option value="cartao_credito">Cartão de Crédito</option>
                <option value="cartao_debito">Cartão de Débito</option>
                <option value="transferencia">Transferência</option>
                <option value="dinheiro">Dinheiro (na oficina)</option>
                <option value="crypto">Criptomoeda</option>
              </select>
            </div>
            <div>
              <Label className="text-xs text-slate-400 uppercase">Observação (opcional)</Label>
              <Input value={observacao} onChange={(e) => setObservacao(e.target.value)}
                placeholder="Ex: PIX enviado às 14h" className="bg-slate-800 border-white/10 text-white text-sm mt-1" />
            </div>
            <div className="bg-blue-500/10 border border-blue-500/30 rounded p-2 text-xs text-blue-200">
              Após enviar, a oficina vai confirmar seu pagamento. Em breve, pagamento online direto pelo site (Stripe/PIX).
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setSolicitando(null)} className="text-slate-400">Cancelar</Button>
              <Button onClick={solicitarPagamento} disabled={enviando} className="bg-blue-500 hover:bg-blue-600 text-white" data-testid="solicitar-pagto-confirmar">
                {enviando ? 'Enviando...' : 'Solicitar Pagamento'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ====== CONTATO ======
export function ClienteContato() {
  const [info, setInfo] = useState(null);
  useEffect(() => { portalApi.get('/cliente/oficina-info').then(({ data }) => setInfo(data)).catch(() => setInfo({})); }, []);
  if (!info) return <div className="p-4 text-slate-400">Carregando...</div>;
  const whats = (info.whatsapp || info.telefone || '').replace(/\D/g, '');
  const waUrl = whats ? `https://wa.me/${whats.startsWith('55') ? whats : '55' + whats}` : null;

  return (
    <div className="p-4 md:p-8 max-w-[1200px] mx-auto" data-testid="portal-contato">
      <PageHeader icon={MessageCircle} titulo="Falar com a Oficina" subtitulo="Tire dúvidas, solicite orçamento ou agende" />
      <div className="bg-slate-900 border border-white/10 rounded-xl p-5 space-y-4">
        <div>
          <p className="text-blue-300/70 text-xs uppercase tracking-wider">Oficina</p>
          <p className="text-white text-lg font-semibold">{info.nome || 'Oficina'}</p>
        </div>
        {info.telefone && (
          <div className="flex items-center gap-3">
            <Phone className="w-4 h-4 text-blue-400" />
            <a href={`tel:${info.telefone}`} className="text-white">{info.telefone}</a>
          </div>
        )}
        {info.email && (
          <div className="flex items-center gap-3">
            <Mail className="w-4 h-4 text-blue-400" />
            <a href={`mailto:${info.email}`} className="text-white break-all">{info.email}</a>
          </div>
        )}
        {info.endereco && (
          <div className="flex items-start gap-3">
            <MapPin className="w-4 h-4 text-blue-400 mt-0.5" />
            <p className="text-white text-sm">{info.endereco}</p>
          </div>
        )}
        {waUrl && (
          <a href={waUrl} target="_blank" rel="noopener noreferrer" className="block">
            <Button className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-6" data-testid="portal-whatsapp-btn">
              <MessageCircle className="w-5 h-5 mr-2" /> Conversar no WhatsApp
            </Button>
          </a>
        )}
      </div>
    </div>
  );
}

// ====== PERFIL (editável + trocar senha) ======
export function ClientePerfil() {
  const [me, setMe] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [savingPerfil, setSavingPerfil] = useState(false);
  const [openSenha, setOpenSenha] = useState(false);
  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [loading, setLoading] = useState(false);

  const carregar = () => portalApi.get('/cliente/me').then(({ data }) => setMe(data.cliente)).catch(() => {});
  useEffect(() => { carregar(); }, []);

  const startEdit = () => {
    setForm({
      nome: me.nome || '',
      telefone: me.telefone || '',
      email: me.email || '',
      endereco: me.endereco || '',
      numero: me.numero || '',
      complemento: me.complemento || '',
      bairro: me.bairro || '',
      cidade: me.cidade || '',
      estado: me.estado || '',
      cep: me.cep || '',
    });
    setEditing(true);
  };

  const salvarPerfil = async () => {
    setSavingPerfil(true);
    try {
      await portalApi.put('/cliente/perfil', form);
      toast.success('Perfil atualizado!');
      setEditing(false);
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao atualizar');
    } finally {
      setSavingPerfil(false);
    }
  };

  const trocar = async (e) => {
    e.preventDefault();
    if (novaSenha.length < 4) { toast.error('Mínimo 4 caracteres'); return; }
    setLoading(true);
    try {
      await portalApi.post('/cliente/auth/change-password', { senha_atual: senhaAtual, nova_senha: novaSenha });
      toast.success('Senha alterada com sucesso');
      setOpenSenha(false); setSenhaAtual(''); setNovaSenha('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao alterar senha');
    } finally {
      setLoading(false);
    }
  };

  if (!me) return <div className="p-4 text-slate-400">Carregando...</div>;

  return (
    <div className="p-4 md:p-8 max-w-[800px] mx-auto" data-testid="portal-perfil">
      <PageHeader titulo="Meu Perfil" subtitulo="Seus dados cadastrais" />

      {editing ? (
        <div className="bg-slate-900 border border-white/10 rounded-xl p-5 space-y-3 mb-5" data-testid="perfil-form">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-slate-400 text-xs uppercase">Nome</Label>
              <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })}
                className="bg-slate-950 border-white/10 text-white" data-testid="perfil-nome" />
            </div>
            <div>
              <Label className="text-slate-400 text-xs uppercase">Telefone</Label>
              <Input value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })}
                className="bg-slate-950 border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-slate-400 text-xs uppercase">E-mail</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="bg-slate-950 border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-slate-400 text-xs uppercase">CEP</Label>
              <Input value={form.cep} onChange={(e) => setForm({ ...form, cep: e.target.value })}
                className="bg-slate-950 border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-slate-400 text-xs uppercase">Cidade / UF</Label>
              <div className="flex gap-2">
                <Input value={form.cidade} onChange={(e) => setForm({ ...form, cidade: e.target.value })}
                  className="bg-slate-950 border-white/10 text-white" placeholder="Cidade" />
                <Input value={form.estado} onChange={(e) => setForm({ ...form, estado: e.target.value.toUpperCase().slice(0, 2) })}
                  className="bg-slate-950 border-white/10 text-white w-16" placeholder="UF" maxLength={2} />
              </div>
            </div>
            <div className="col-span-2">
              <Label className="text-slate-400 text-xs uppercase">Endereço</Label>
              <Input value={form.endereco} onChange={(e) => setForm({ ...form, endereco: e.target.value })}
                className="bg-slate-950 border-white/10 text-white" placeholder="Rua, avenida" />
            </div>
            <div>
              <Label className="text-slate-400 text-xs uppercase">Número</Label>
              <Input value={form.numero} onChange={(e) => setForm({ ...form, numero: e.target.value })}
                className="bg-slate-950 border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-slate-400 text-xs uppercase">Bairro</Label>
              <Input value={form.bairro} onChange={(e) => setForm({ ...form, bairro: e.target.value })}
                className="bg-slate-950 border-white/10 text-white" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
            <Button variant="ghost" onClick={() => setEditing(false)} className="text-slate-400">Cancelar</Button>
            <Button onClick={salvarPerfil} disabled={savingPerfil} className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="perfil-salvar">
              {savingPerfil ? 'Salvando...' : 'Salvar Alterações'}
            </Button>
          </div>
        </div>
      ) : (
        <div className="bg-slate-900 border border-white/10 rounded-xl p-5 space-y-3 mb-5">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Nome</p>
              <p className="text-white font-medium">{me.nome}</p>
            </div>
            <Button onClick={startEdit} variant="ghost" size="sm" className="text-blue-400 hover:bg-blue-500/10" data-testid="perfil-editar-btn">
              <Edit className="w-4 h-4 mr-1" /> Editar
            </Button>
          </div>
          {me.cpf_cnpj && (
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider">CPF</p>
              <p className="text-white">{me.cpf_cnpj}</p>
            </div>
          )}
          {me.telefone && (
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Telefone</p>
              <p className="text-white">{me.telefone}</p>
            </div>
          )}
          {me.email && (
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider">E-mail</p>
              <p className="text-white break-all">{me.email}</p>
            </div>
          )}
          {(me.endereco || me.cidade) && (
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Endereço</p>
              <p className="text-white text-sm">
                {[me.endereco, me.numero, me.bairro, me.cidade, me.estado, me.cep].filter(Boolean).join(', ')}
              </p>
            </div>
          )}
        </div>
      )}

      <Button onClick={() => setOpenSenha(true)} className="w-full bg-blue-600 hover:bg-blue-500 text-white" data-testid="portal-trocar-senha-btn">
        <KeyRound className="w-4 h-4 mr-2" /> Alterar Senha
      </Button>

      <Dialog open={openSenha} onOpenChange={setOpenSenha}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Alterar Senha</DialogTitle>
            <DialogDescription className="text-slate-400">Informe sua senha atual e escolha uma nova.</DialogDescription>
          </DialogHeader>
          <form onSubmit={trocar} className="space-y-3">
            <div>
              <Label className="text-slate-400 text-xs">Senha Atual</Label>
              <Input type="password" value={senhaAtual} onChange={(e) => setSenhaAtual(e.target.value)} className="bg-slate-950 border-white/10 text-white" required data-testid="portal-senha-atual" />
            </div>
            <div>
              <Label className="text-slate-400 text-xs">Nova Senha (mín. 4)</Label>
              <Input type="password" value={novaSenha} onChange={(e) => setNovaSenha(e.target.value)} className="bg-slate-950 border-white/10 text-white" required minLength={4} data-testid="portal-nova-senha" />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpenSenha(false)} className="text-slate-400">Cancelar</Button>
              <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="portal-salvar-senha">{loading ? 'Salvando...' : 'Alterar'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
